/**
 * Local vault mutations (zero-knowledge).
 *
 * Writes OPAQUE rows to PowerSync (local SQLite) uploaded via
 * `POST /api/sync/batch`. Server only sees encrypted blobs + minimal
 * metadata: encryption/decryption happens on client with `@boveda/shared`
 * crypto BEFORE calling here. These functions never touch plaintext.
 */

import {
  type PowerSyncDrizzleDB,
  vault,
  vault_account,
  vault_attachment,
  vault_item,
} from "@boveda/powersync";
import type { ProtectedAccount, ProtectedAttachment, ProtectedItem } from "../crypto/types";
import { nowISO } from "../utils/date-helpers";
import { patchRowVerified } from "../powersync/verified-write";

/**
 * Creates user account root row (vault setup, once).
 *
 * Previously this function decided INSERT-vs-UPDATE via preceding `SELECT` — the
 * SAME ghost `UPDATE` window from read side: a `SELECT`
 * running right when PowerSync rebuilds view can observe missing
 * row and retry INSERT on an `id` that already exists. Split
 * into explicit `create`/`update` because the single caller
 * (`VaultContext.setup`, running only when `status() === "needs-setup"`,
 * i.e. when row does NOT exist) already knows this is a creation: no
 * ambiguity to resolve with `SELECT`. Update branch was dead
 * (no caller rotated master password by rewriting this row)
 * and is removed rather than protected — guarding dead code closes no
 * real gap. If master password rotation is added in future,
 * that write must go through `patchRowVerified` explicitly, like
 * `updateVaultItemMutation` below.
 */
export async function createVaultAccountMutation(
  db: PowerSyncDrizzleDB,
  account: ProtectedAccount,
  recoveryBlob?: string | null,
): Promise<void> {
  const now = nowISO();
  // PowerSync keys by `id`; for vault_account we use id === userId.
  await db.insert(vault_account).values({
    id: account.userId,
    user_id: account.userId,
    kdf: JSON.stringify(account.kdf),
    protected_account_key: account.protectedAccountKey,
    protected_private_key: account.protectedPrivateKey,
    public_key: account.publicKey,
    auth_hash: account.authHash,
    recovery_blob: recoveryBlob ?? null,
    created_at: now,
    updated_at: now,
  });
}

export interface CreatePersonalVaultInput {
  id: string;
  ownerUserId: string;
  /** Encrypted name (envelope) — preferred — or null if stored plain outside. */
  nameEnvelope: string | null;
  /** Vault key wrapped with account key. */
  protectedKey: string;
}

/** Creates local PERSONAL vault. */
export async function createPersonalVaultMutation(
  db: PowerSyncDrizzleDB,
  input: CreatePersonalVaultInput,
): Promise<string> {
  const now = nowISO();
  await db.insert(vault).values({
    id: input.id,
    type: "personal",
    owner_user_id: input.ownerUserId,
    organization_id: null,
    name_envelope: input.nameEnvelope,
    protected_key: input.protectedKey,
    key_epoch: 0,
    created_at: now,
    updated_at: now,
    deleted_at: null,
  });
  return input.id;
}

/**
 * Creates a new encrypted item. `item` already comes from `crypto.encryptItem` (envelope
 * opaque + rev=1).
 *
 * Previously this branch and edit lived in a single `upsert` deciding
 * INSERT-vs-UPDATE via preceding `SELECT id`: SAME ghost
 * `UPDATE` window from read side — creating an item and editing right
 * after (renaming right after save, standard flow on new secret)
 * could observe newly created row as missing and retry
 * INSERT on an `id` that already exists. Split because caller
 * (`VaultContext.saveItem`/`saveItemWith`) already knows if `existing` is present —
 * no guessing needed via `SELECT`.
 */
export async function createVaultItemMutation(
  db: PowerSyncDrizzleDB,
  item: ProtectedItem,
): Promise<string> {
  const now = nowISO();
  await db.insert(vault_item).values({
    id: item.id,
    vault_id: item.vaultId,
    envelope: item.envelope,
    rev: item.rev,
    created_at: now,
    updated_at: now,
    deleted_at: null,
  });
  return item.id;
}

/**
 * Updates an EXISTING encrypted item (credential rotation, note edit).
 * Verified: if `UPDATE` matches zero rows (row still absent from view
 * after recent checkpoint) re-reads and retries with backoff instead of
 * faking success — silently losing write is worst possible failure
 * in secret manager: user believes new credential saved
 * when old one remains synchronized.
 *
 * `scope: vault_id = item.vaultId` (§4.14 in `infra/PENDING_WORK.md`):
 * `infra/sync_streams.yaml` (`org_vaults`) syncs `vault_item` from
 * ALL org vaults of user, not only currently open one;
 * an `id` of an item from ANOTHER vault (URL, sync) would match bare
 * `WHERE id = ?`. `vault_item` lacks its own `organization_id` —
 * row-level tenant boundary is `vault_id` (already on `item`, without
 * new parameter).
 */
export async function updateVaultItemMutation(
  db: PowerSyncDrizzleDB,
  item: ProtectedItem,
): Promise<string> {
  await patchRowVerified(
    db,
    vault_item,
    vault_item.id,
    item.id,
    {
      envelope: item.envelope,
      rev: item.rev,
      deleted_at: null,
    },
    { scope: [{ column: vault_item.vault_id, value: item.vaultId }] },
  );
  return item.id;
}

/**
 * Soft-delete of an item (trash). Verified: see `updateVaultItemMutation`.
 * `vaultId` is provided by caller (already known: active vault) and
 * passed as `scope` for same reason — see `updateVaultItemMutation`.
 */
export async function deleteVaultItemMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  vaultId: string,
): Promise<void> {
  await patchRowVerified(
    db,
    vault_item,
    vault_item.id,
    id,
    { deleted_at: nowISO() },
    { scope: [{ column: vault_item.vault_id, value: vaultId }] },
  );
}

/**
 * Restores soft-deleted item. Verified + `scope`: see
 * `updateVaultItemMutation`/`deleteVaultItemMutation`.
 */
export async function restoreVaultItemMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  vaultId: string,
): Promise<void> {
  await patchRowVerified(
    db,
    vault_item,
    vault_item.id,
    id,
    { deleted_at: null },
    { scope: [{ column: vault_item.vault_id, value: vaultId }] },
  );
}

/**
 * Inserts an encrypted attachment. `attachment` originates from `crypto.encryptAttachment`
 * (3 opaque blobs). Server only guards ciphertext.
 */
export async function insertVaultAttachmentMutation(
  db: PowerSyncDrizzleDB,
  attachment: ProtectedAttachment,
): Promise<string> {
  const now = nowISO();
  await db.insert(vault_attachment).values({
    id: attachment.id,
    vault_id: attachment.vaultId,
    item_id: attachment.itemId,
    wrapped_key: attachment.wrappedKey,
    meta_envelope: attachment.metaEnvelope,
    blob: attachment.blob,
    size: attachment.size,
    created_at: now,
    updated_at: now,
    deleted_at: null,
  });
  return attachment.id;
}

/**
 * Soft-delete of attachment. Verified + `scope`: see
 * `updateVaultItemMutation` — `vault_attachment` also lacks its
 * own `organization_id`, row boundary is likewise `vault_id`.
 */
export async function deleteVaultAttachmentMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  vaultId: string,
): Promise<void> {
  await patchRowVerified(
    db,
    vault_attachment,
    vault_attachment.id,
    id,
    { deleted_at: nowISO() },
    { scope: [{ column: vault_attachment.vault_id, value: vaultId }] },
  );
}
